import React from 'react';
import { InputLabel } from '@mui/material';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { StyledButton, StyledButtonBox } from '../index.styles';
import { mapError } from 'src/utils/validation';
import {
  FormErrors,
  RegistrationRabenSchema,
  validateForm,
  validateFormFirstPage,
} from '../form';
import { Country } from '../types';
import Consents from '../components/Consents';
import { useLanguage, useSupportedCountries } from 'src/common/hooks';

export default function RegistrationFirstPage(props: {
  form: RegistrationRabenSchema;
  setForm: React.Dispatch<React.SetStateAction<RegistrationRabenSchema>>;
  errors: FormErrors;
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>;
  onNext: () => void;
}) {
  const { form, setForm, errors, setErrors, onNext } = props;
  const { t } = useLanguage();

  const { data: countries } = useSupportedCountries();

  const isError = (fieldName: keyof RegistrationRabenSchema) =>
    !!form[fieldName] && !!mapError(errors, fieldName, form[fieldName]);
  const errorText = (fieldName: keyof RegistrationRabenSchema) =>
    !!form[fieldName] &&
    String(t(mapError(errors, fieldName, form[fieldName]) || ''));

  return (
    <>
      {[
        'first_name',
        'last_name',
        'email',
        'phone_number',
        'organization_name',
        'vat_identifier',
      ].map((field) => (
        <TextField
          key={field}
          sx={{ mt: '24px' }}
          label={t(`registration_raben.form.${field}`)}
          type="text"
          value={form[field as keyof RegistrationRabenSchema]}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const newForm = {
              ...form,
              [field]: e.currentTarget.value,
            };
            setForm(newForm);
            setErrors(validateForm(newForm));
          }}
          variant="outlined"
          fullWidth
          required
          error={isError(field as keyof RegistrationRabenSchema)}
          helperText={errorText(field as keyof RegistrationRabenSchema)}
          disabled={field === 'vat_identifier'}
        />
      ))}
      <FormControl required fullWidth sx={{ mt: '24px' }}>
        <InputLabel id="countryInputLabel">
          {t('registration_raben.form.country')}
        </InputLabel>
        <Select
          labelId="countryInputLabel"
          label={t('registration_raben.form.country')}
          id="countrySelect"
          value={form.country}
          defaultValue={form.country}
          onChange={(e: SelectChangeEvent<string | null>) =>
            setForm({ ...form, country: e.target.value })
          }
          error={isError('country')}
        >
          {countries?.map((country: Country) => (
            <MenuItem key={country.alpha3} value={country.alpha3}>
              {country.name} ({country.alpha3})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Consents form={form} setForm={setForm} />

      <StyledButtonBox sx={{ justifyContent: 'right' }}>
        <StyledButton
          variant="contained"
          size="large"
          onClick={onNext}
          disabled={Object.keys(validateFormFirstPage(form)).length > 0}
          endIcon={<NavigateNextIcon fontSize="large" />}
        >
          {t('registration_raben.nextPage')}
        </StyledButton>
      </StyledButtonBox>
    </>
  );
}
